'use client';
import React, { useState } from 'react';
import { Plus, Trash2, Tag, ChevronDown, ChevronUp, Edit2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const apartments = [
  { id: 'apt-001', name: 'Sunset Terrace Suite', baseRate: 180 },
  { id: 'apt-002', name: 'Ocean Breeze Loft', baseRate: 150 },
  { id: 'apt-003', name: 'Garden Villa Escape', baseRate: 200 },
  { id: 'apt-004', name: 'Rooftop Haven', baseRate: 165 },
];

interface RateRule {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  rate: number;
  type: 'increase' | 'decrease' | 'fixed';
}

interface ApartmentRates {
  [aptId: string]: RateRule[];
}

const initialRates: ApartmentRates = {
  'apt-001': [
    { id: 'r1', label: 'Summer Peak', startDate: '2026-06-15', endDate: '2026-08-31', rate: 220, type: 'fixed' },
    { id: 'r2', label: 'Christmas Week', startDate: '2026-12-22', endDate: '2026-12-31', rate: 260, type: 'fixed' },
  ],
  'apt-002': [
    { id: 'r3', label: 'Long Weekend', startDate: '2026-07-04', endDate: '2026-07-06', rate: 185, type: 'fixed' },
  ],
  'apt-003': [],
  'apt-004': [
    { id: 'r4', label: 'New Year', startDate: '2026-12-30', endDate: '2027-01-02', rate: 210, type: 'fixed' },
  ],
};

const TYPE_COLORS: Record<string, string> = {
  fixed: 'bg-blue-50 text-blue-700 border-blue-200',
  increase: 'bg-amber-50 text-amber-700 border-amber-200',
  decrease: 'bg-green-50 text-green-700 border-green-200',
};

const TYPE_LABELS: Record<string, string> = {
  fixed: 'Fixed Rate',
  increase: 'Seasonal Increase',
  decrease: 'Discount',
};

function formatDate(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface AddRuleFormProps {
  aptId: string;
  onAdd: (aptId: string, rule: RateRule) => void;
  onCancel: () => void;
}

function AddRuleForm({ aptId, onAdd, onCancel }: AddRuleFormProps) {
  const [label, setLabel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rate, setRate] = useState('');
  const [type, setType] = useState<'increase' | 'decrease' | 'fixed'>('fixed');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !startDate || !endDate || !rate) {
      toast.error('Please fill in all fields.');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date.');
      return;
    }
    const rateNum = parseFloat(rate);
    if (isNaN(rateNum) || rateNum <= 0) {
      toast.error('Please enter a valid rate.');
      return;
    }
    onAdd(aptId, {
      id: `r-${Date.now()}`,
      label: label.trim(),
      startDate,
      endDate,
      rate: rateNum,
      type,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-4 bg-muted/50 rounded-xl border border-border space-y-3">
      <p className="text-xs font-700 text-foreground uppercase tracking-wide">New Rate Rule</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">Label</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Summer Peak"
            className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as 'increase' | 'decrease' | 'fixed')}
            className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          >
            <option value="fixed">Fixed Rate</option>
            <option value="increase">Seasonal Increase</option>
            <option value="decrease">Discount</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-600 text-muted-foreground mb-1">Rate per Night ($)</label>
          <input
            type="number"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="e.g. 220"
            min="1"
            step="0.01"
            className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 text-xs font-600 px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground"
        >
          <X size={13} /> Cancel
        </button>
        <button
          type="submit"
          className="flex items-center gap-1.5 text-xs font-600 px-3 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
        >
          <Check size={13} /> Save Rule
        </button>
      </div>
    </form>
  );
}

interface EditRuleFormProps {
  rule: RateRule;
  onSave: (updated: RateRule) => void;
  onCancel: () => void;
}

function EditRuleForm({ rule, onSave, onCancel }: EditRuleFormProps) {
  const [label, setLabel] = useState(rule.label);
  const [startDate, setStartDate] = useState(rule.startDate);
  const [endDate, setEndDate] = useState(rule.endDate);
  const [rate, setRate] = useState(String(rule.rate));
  const [type, setType] = useState<'increase' | 'decrease' | 'fixed'>(rule.type);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !startDate || !endDate || !rate) {
      toast.error('Please fill in all fields.');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date.');
      return;
    }
    const rateNum = parseFloat(rate);
    if (isNaN(rateNum) || rateNum <= 0) {
      toast.error('Please enter a valid rate.');
      return;
    }
    onSave({ ...rule, label: label.trim(), startDate, endDate, rate: rateNum, type });
  };

  return (
    <form onSubmit={handleSubmit} className="mt-2 p-3 bg-muted/50 rounded-xl border border-border space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">Label</label>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">Type</label>
          <select value={type} onChange={(e) => setType(e.target.value as 'increase' | 'decrease' | 'fixed')} className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors">
            <option value="fixed">Fixed Rate</option>
            <option value="increase">Seasonal Increase</option>
            <option value="decrease">Discount</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">Start Date</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">End Date</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-600 text-muted-foreground mb-1">Rate per Night ($)</label>
          <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} min="1" step="0.01" className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="flex items-center gap-1.5 text-xs font-600 px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground">
          <X size={13} /> Cancel
        </button>
        <button type="submit" className="flex items-center gap-1.5 text-xs font-600 px-3 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors">
          <Check size={13} /> Update
        </button>
      </div>
    </form>
  );
}

export default function DateRatePricing() {
  const [rates, setRates] = useState<ApartmentRates>(initialRates);
  const [expandedApt, setExpandedApt] = useState<string | null>('apt-001');
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<{ aptId: string; ruleId: string } | null>(null);

  const handleAddRule = (aptId: string, rule: RateRule) => {
    setRates((prev) => ({
      ...prev,
      [aptId]: [...(prev[aptId] || []), rule],
    }));
    setAddingFor(null);
    toast.success(`Rate rule "${rule.label}" added successfully.`);
  };

  const handleDeleteRule = (aptId: string, ruleId: string) => {
    const rule = rates[aptId]?.find((r) => r.id === ruleId);
    setRates((prev) => ({
      ...prev,
      [aptId]: prev[aptId].filter((r) => r.id !== ruleId),
    }));
    toast.success(`Rate rule "${rule?.label}" removed.`);
  };

  const handleEditRule = (aptId: string, updated: RateRule) => {
    setRates((prev) => ({
      ...prev,
      [aptId]: prev[aptId].map((r) => (r.id === updated.id ? updated : r)),
    }));
    setEditingRule(null);
    toast.success(`Rate rule "${updated.label}" updated.`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-700 text-foreground">Custom Date Rates</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Set special pricing for specific date ranges per apartment</p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 rounded-lg">
          <Tag size={14} className="text-primary" />
          <span className="text-xs font-600 text-primary">
            {Object.values(rates).reduce((sum, arr) => sum + arr.length, 0)} active rules
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {apartments.map((apt) => {
          const aptRules = rates[apt.id] || [];
          const isExpanded = expandedApt === apt.id;
          const isAdding = addingFor === apt.id;

          return (
            <div key={apt.id} className="card-base overflow-hidden">
              {/* Apartment Header */}
              <button
                onClick={() => setExpandedApt(isExpanded ? null : apt.id)}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/30 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-700 text-sm text-foreground">{apt.name}</span>
                    <span className="text-xs text-muted-foreground font-500">Base: ${apt.baseRate}/night</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {aptRules.length === 0 ? (
                      <span className="text-xs text-muted-foreground">No custom rates — using base rate</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {aptRules.slice(0, 3).map((rule) => (
                          <span
                            key={rule.id}
                            className={`text-[10px] font-600 px-2 py-0.5 rounded-full border ${TYPE_COLORS[rule.type]}`}
                          >
                            {rule.label} · ${rule.rate}
                          </span>
                        ))}
                        {aptRules.length > 3 && (
                          <span className="text-[10px] font-600 px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                            +{aptRules.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-600 text-muted-foreground tabular-nums">
                    {aptRules.length} rule{aptRules.length !== 1 ? 's' : ''}
                  </span>
                  {isExpanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-border">
                  {/* Rules List */}
                  {aptRules.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {aptRules.map((rule) => {
                        const isEditing = editingRule?.aptId === apt.id && editingRule?.ruleId === rule.id;
                        return (
                          <div key={rule.id}>
                            {isEditing ? (
                              <EditRuleForm
                                rule={rule}
                                onSave={(updated) => handleEditRule(apt.id, updated)}
                                onCancel={() => setEditingRule(null)}
                              />
                            ) : (
                              <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl border border-border/60 group">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-700 text-foreground">{rule.label}</span>
                                    <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full border ${TYPE_COLORS[rule.type]}`}>
                                      {TYPE_LABELS[rule.type]}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 mt-1">
                                    <span className="text-xs text-muted-foreground">
                                      {formatDate(rule.startDate)} – {formatDate(rule.endDate)}
                                    </span>
                                    <span className="text-xs font-700 text-primary">${rule.rate}/night</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => setEditingRule({ aptId: apt.id, ruleId: rule.id })}
                                    className="p-1.5 rounded-lg hover:bg-blue-50 text-muted-foreground hover:text-blue-600 transition-colors"
                                    title="Edit rule"
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRule(apt.id, rule.id)}
                                    className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"
                                    title="Delete rule"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-3 py-6 text-center">
                      <Tag size={24} className="mx-auto text-muted-foreground/40 mb-2" />
                      <p className="text-sm text-muted-foreground">No custom rates set</p>
                      <p className="text-xs text-muted-foreground/70 mt-0.5">Add a rule to override the base rate for specific dates</p>
                    </div>
                  )}

                  {/* Add Rule Form or Button */}
                  {isAdding ? (
                    <AddRuleForm
                      aptId={apt.id}
                      onAdd={handleAddRule}
                      onCancel={() => setAddingFor(null)}
                    />
                  ) : (
                    <button
                      onClick={() => { setAddingFor(apt.id); setEditingRule(null); }}
                      className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-primary/40 text-primary text-xs font-600 hover:bg-primary/5 transition-colors"
                    >
                      <Plus size={14} /> Add Rate Rule
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
